import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLoading } from '../../../../components/LoadingProvider';
import api from '../../../../api/axios';
import { getIndStates, calculateAge, getCountryCodes } from '../../../../utils/common';
import useAuth from '../../../../hooks/useAuth';
import { showSuccess, showError } from '../../../../utils/toast';
import { useDebouncedCallback } from 'use-debounce';
import { getPublicImgUrl } from '../../../../utils/common';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import moment from 'moment';

export default function BookAppointment() {
    const { setIsLoading } = useLoading();
    const indStates = getIndStates();
    const countryCodes = getCountryCodes();
    const MySwal = withReactContent(Swal);
    const reportFileInputRef = useRef(null);
    const errorDivRef = useRef(null);
    const navigate = useNavigate();
    const [ searchParams, setSearchParams ] = useSearchParams();
    const [ doctorSearchText, setDoctorSearchText ] = useState('');
    const [ nextBtnDisabled, setNextBtnDisabled ] = useState(false);
    const { user, isAuthenticated, userRole } = useAuth({ redirectPath: '/login', allowedRoles: ['patient'] });

    const [ doctorList, setDoctorList ] = useState([]);
    const [ selectedDoctor, setSelectedDoctor ] = useState(null);
    const [ selectedSlot, setSelectedSlot ] = useState(null);
    const [ currentStep, setCurrentStep ] = useState(1);
    const [ appointmentId, setAppointmentId ] = useState('');
    const [ appointmentData, setAppointmentData ] = useState({});
    const [ appointmentPaymentId, setAppointmentPaymentId ] = useState('');
    const [ paymentInitData, setPaymentInitData ] = useState(null);
    const [ paymentMethod, setPaymentMethod ] = useState('razorpay');
    const [ medicalQuestions, setMedicalQuestions ] = useState({
        visit_reason: '',
        name: user?.name || '',
        dob: '',
        gender: '',
        address: {
            line1: '',
            line2: '',
            district: '',
            state: '',
            country: 'India',
            pincode: ''
        },
        email: '',
        phoneCode: '',
        phoneNumber: '',
        contactNumber: user?.phone || '',
        allergy: '',
        regularMedicines: '',
        previousReports: []
    });
    const [ medicalQuestionError, setMedicalQuestionError ] = useState({
        visit_reason: '',
        name: '',
        dob: '',
        gender: '',
        address: {
            line1: '',
            district: '',
            state: '',
            country: '',
            pincode: ''
        },
    });

    // const today = new Date();
    const date = new Date();
    const offset = date.getTimezoneOffset() == 0 ? 0 : -1 * date.getTimezoneOffset();
    let normalized = new Date(date.getTime() + (offset) * 60000);
    let today = new Date(normalized.toLocaleString("en-US", {timeZone: "Asia/Calcutta"}));
    const [selectedDate, setSelectedDate] = useState(today.toISOString().substring(0, 10));
    const [selectedDoctorDate, setSelectedDoctorDate] = useState({});
    const [previousSelectedDoctor, setPreviousSelectedDoctor] = useState(null);

    const debounced = useDebouncedCallback(async (value) => {
        setNextBtnDisabled(true);
        setIsLoading(true);
        setDoctorList([]);
        setDoctorSearchText(value);
        try {
            const now = new Date();
            let date = selectedDate+' '+now.getHours()+':'+now.getMinutes()+':'+now.getSeconds();
            const response = await api.get('/doctor/search?text='+value+'&date='+date);
            if (response?.data?.result === 'success') {
                response?.data?.data?.doctors.forEach((doctor, index, arr) => {
                    if(Object.keys(doctor.available_slots).length > 0){
                        for (let key of Object.keys(doctor.available_slots)) {
                            setSelectedDoctorDate({
                                ...selectedDoctorDate,
                                [doctor.id]: key
                            });
                            break;
                        }
                    }
                });
                setDoctorList(response?.data?.data?.doctors || []);
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError(response?.data?.message || 'An error occurred');
            }
        } catch (error) {
            showError(error.message);
        } finally {
            setNextBtnDisabled(false);
            setIsLoading(false);
            setSelectedSlot(null);
        }
    }, 1000);

    const fetchDoctorById = async (id) => {
        setNextBtnDisabled(true);
        setIsLoading(true);
        setDoctorList([]);
        setDoctorSearchText('');
        try {
            const now = new Date();
            let date = selectedDate+' '+now.getHours()+':'+now.getMinutes()+':'+now.getSeconds();
            const response = await api.get('/doctor/search?id='+id+'&date='+date);
            if (response?.data?.result === 'success') {
                response?.data?.data?.doctors.forEach((doctor, index, arr) => {
                    if(Object.keys(doctor.available_slots).length > 0){
                        for (let key of Object.keys(doctor.available_slots)) {
                            setSelectedDoctorDate({
                                ...selectedDoctorDate,
                                [doctor.id]: key
                            });
                            break;
                        }
                    }
                });
                setDoctorList(response?.data?.data?.doctors || []);
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError(response?.data?.message || 'An error occurred');
            }
        } catch (error) {
            showError(error.message);
        } finally {
            setNextBtnDisabled(false);
            setIsLoading(false);
            setSelectedSlot(null);
        }
    }

    const fetchPatientData = async () => {
        setNextBtnDisabled(true);
        try {
            const response = await api.get('/'+userRole+'/details');
            if (response?.data?.result === 'success') {
                setMedicalQuestions({
                    visit_reason: '',
                    name: response.data.data.name || '',
                    dob: response.data.data.dob || '',
                    gender: response.data.data.gender || '',
                    address: {
                        line1: response.data.data.address_line1 || '',
                        line2: response.data.data.address_line2 || '',
                        district: response.data.data.district || '',
                        state: response.data.data.state || '',
                        country: response.data.data.country || '',
                        pincode: response.data.data.pincode || '',
                    },
                    email: response.data.data.email || '',
                    phoneCode: response.data.data.phone_code || '',
                    phoneNumber: response.data.data.phone_number || '',
                    contactNumber: '',
                    allergy: response.data.data.allergies || '',
                    regularMedicines: response.data.data.regular_medicines || '',
                    // previousReports: response.data.data.documents || [],
                });
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Something went wrong! Please try again later.');
            if (error.response?.status === 401) {
                navigate('/login');
            }
        } finally {
            setNextBtnDisabled(false);
        }
    };

    const fetchAppointmentDetails = async (appointmentId) => {
        setNextBtnDisabled(true);
        try {
            const response = await api.get('/'+userRole+'/appointment/'+appointmentId);
            if (response?.data?.result === 'success') {
                setAppointmentId(response?.data?.data?.appointment?.id);
                setAppointmentData(response?.data?.data?.appointment || {});
                setMedicalQuestions({
                    visit_reason: response?.data?.data?.appointment?.visit_reason || '',
                    name: response?.data?.data?.appointment?.patient_name || '',
                    dob: response?.data?.data?.appointment?.patient_dob || '',
                    gender: response?.data?.data?.appointment?.patient_gender || '',
                    address: {
                        line1: response?.data?.data?.appointment?.patient_address_line1 || '',
                        line2: response?.data?.data?.appointment?.patient_address_line2 || '',
                        district: response?.data?.data?.appointment?.patient_district || '',
                        state: response?.data?.data?.appointment?.patient_state || '',
                        country: response?.data?.data?.appointment?.patient_country || '',
                        pincode: response?.data?.data?.appointment?.patient_pincode || '',
                    },
                    email: response?.data?.data?.appointment?.patient_email || '',
                    phoneCode: response?.data?.data?.patient?.phone_code || '',
                    phoneNumber: response?.data?.data?.patient?.phone_number || '',
                    contactNumber: '',
                    allergy: response?.data?.data?.appointment?.patient_allergies || '',
                    regularMedicines: response?.data?.data?.appointment?.patient_regular_medicines || '',
                    previousReports: response?.data?.data?.appointment?.patient_reports || [],
                });
                setSelectedDoctor(response?.data?.data?.doctor);
                setSelectedSlot(response?.data?.data?.doctor?.available_slots || null);
                setPreviousSelectedDoctor(response?.data?.data?.doctor);
                setPaymentMethod(response?.data?.data?.appointment?.payment_method || 'phi');
                setPaymentInitData({
                    appointment_id: response?.data?.data?.appointment?.id,
                    doctor_name: response?.data?.data?.doctor?.name || '',
                    consultation_fee: response?.data?.data?.appointment?.total_amount || response?.data?.data?.doctor?.consultation_fee || '',
                    payment_method: response?.data?.data?.appointment?.payment_method || 'phi',
                    payment_url: response?.data?.data?.appointment?.payment_url || null,
                });
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Something went wrong! Please try again later.');
            if (error.response?.status === 401) {
                navigate('/login');
            }
        } finally {
            setNextBtnDisabled(false);
        }
    };

    useEffect(() => {
        document.title = 'Varosa - Book Appointments';
        setIsLoading(false);
        fetchPatientData();
        if(searchParams.get('t')){
            debounced(searchParams.get('t'));
        }else if(searchParams.get('d')){
            fetchDoctorById(searchParams.get('d'));
        }
        if(searchParams.get('a')){
            fetchAppointmentDetails(searchParams.get('a'));
        }
        if(searchParams.get('err')){
            showError(searchParams.get('err'));
        }
    }, []);
    useEffect(() => {
        switch(appointmentData.booking_status){
            case 'initiated':
            case 'payment failed':
            case 'payment initiated':
                handleNextStep(2);
                break;
            case 'payment paid':
                setCurrentStep(3);
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
                break;
            default:
                handleNextStep(1);
                break;
        }
    }, [previousSelectedDoctor]);

    const validateMedicalQuestionsForm = () => {
        return true;
        // let all_good = true;
        // let error = {
        //     visit_reason: '',
        //     name: '',
        //     dob: '',
        //     gender: '',
        //     address: {
        //         line1: '',
        //         district: '',
        //         state: '',
        //         country: '',
        //         pincode: ''
        //     },
        //     email: '',
        // };
        // if(!selectedDoctor || !selectedSlot){
        //     showError('Please select a doctor and time slot.');
        //     return false;
        // }
        // if(medicalQuestions.visit_reason.trim() === ''){
        //     error.visit_reason = 'Please enter a valid visit reason.';
        //     all_good = false;
        // }
        // if(medicalQuestions.name.trim() === '' || medicalQuestions.name.trim().length < 3){
        //     error.name = 'Please enter a valid name.';
        //     all_good = false;
        // }
        // if(medicalQuestions.dob.trim() === '' || calculateAge(medicalQuestions.dob.trim()) < 18){
        //     error.dob = 'Please enter a valid DOB (at least 18 years old).';
        //     all_good = false;
        // }
        // if(medicalQuestions.gender.trim() === ''){
        //     error.dob = 'Please select a valid gender.';
        //     all_good = false;
        // }
        // if(medicalQuestions.address.line1.trim() === ''){
        //     error.address.line1 = 'Please enter a valid address.';
        //     all_good = false;
        // }
        // if(medicalQuestions.address.district.trim() === ''){
        //     error.address.district = 'Please enter a valid district.';
        //     all_good = false;
        // }
        // if(medicalQuestions.address.state.trim() === ''){
        //     error.address.state = 'Please select a valid state.';
        //     all_good = false;
        // }
        // if(medicalQuestions.address.country.trim() === ''){
        //     error.address.country = 'Please enter a valid country.';
        //     all_good = false;
        // }
        // if(medicalQuestions.address.pincode.trim().replace(/\D/g, '').length !== 6){
        //     error.address.pincode = 'Please enter a valid pincode (6-digits).';
        //     all_good = false;
        // }
        // if(!/^\S+@\S+\.\S+$/.test(medicalQuestions.email.trim())){
        //     error.email = 'Please enter a valid email.';
        //     all_good = false;
        // }
        // setMedicalQuestionError(error);
        // return all_good;
    };

    const handleMedicalQuestionChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setMedicalQuestions(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setMedicalQuestions(prev => ({
                ...prev,
                [name]: value
            }));
        }
    }

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        for(let file of files){
            const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
            if (!validTypes.includes(file.type)) {
                showError('Please upload a PDF, PNG, JPEG file.');
                reportFileInputRef.current.value = '';
                return;
            }
            if(file.size > 2 * 1024 * 1024){
                showError('File size exceeds 2MB. Please choose a smaller file.');
                reportFileInputRef.current.value = '';
                return;
            }
        }
        setMedicalQuestions(prev => ({
            ...prev,
            previousReports: ((Array.isArray(prev.previousReports) ? [...prev.previousReports, ...files] : [...files]) || []),
        }));
        reportFileInputRef.current.value = '';
    };

    const handleRemoveFile = (index) => {
        const updatedReports = medicalQuestions.previousReports.filter((_, i) => i !== index);
        setMedicalQuestions(prev => ({
            ...prev,
            previousReports: updatedReports,
        }));
    };

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const viewReport = async (report, isItUrl = false) => {
        try{
            if(isItUrl){
                MySwal.fire({
                    title: 'Report',
                    html: `<iframe src="${report}" alt="Report" style="width: 100%;height: 600px;"></iframe>`,
                    width: '90%',
                    showConfirmButton: true,
                    confirmButtonText: 'Close',
                    customClass: {
                        container: '',
                    },
                });
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                const base64String = reader.result;
                if(base64String){
                    MySwal.fire({
                        title: 'Report',
                        html: `<iframe src="${base64String}" alt="Report" style="width: 100%;height: 600px;"></iframe>`,
                        width: '90%',
                        showConfirmButton: true,
                        confirmButtonText: 'Close',
                        customClass: {
                            container: '',
                        },
                    });
                }
            };
            reader.readAsDataURL(report);
        }catch(error){
            console.log(error);
            showError('Something went wrong! Please try again later.');
            return;
        }
    };

    const handleMedicalQuestionsSubmit = async (next_step) => {
        try {
            if(!validateMedicalQuestionsForm()){ 
                errorDivRef.current.scrollIntoView({ behavior: 'smooth' });
                return false;
            }
            const now = new Date();
            const postData = new FormData();
            postData.append('appointment_id', appointmentId);
            postData.append('doctor_id', selectedDoctor.id);
            postData.append('slot_id', selectedSlot.id);
            postData.append('book_date', selectedDate+' '+now.getHours()+':'+now.getMinutes()+':'+now.getSeconds());
            postData.append('visit_reason', medicalQuestions.visit_reason);
            postData.append('name', medicalQuestions.name);
            postData.append('dob', medicalQuestions.dob);
            postData.append('gender', medicalQuestions.gender);
            postData.append('address_line1', medicalQuestions.address.line1);
            postData.append('address_line2', medicalQuestions.address.line2);
            postData.append('district', medicalQuestions.address.district);
            postData.append('state', medicalQuestions.address.state);
            postData.append('country', medicalQuestions.address.country);
            postData.append('pincode', medicalQuestions.address.pincode);
            postData.append('email', medicalQuestions.email);
            postData.append('allergies', medicalQuestions.allergy);
            postData.append('regular_medicines', medicalQuestions.regularMedicines);
            postData.append('mode', 'Website');
            if (Array.isArray(medicalQuestions.previousReports)) {
                for(let report of medicalQuestions.previousReports){
                    if(report.id){
                        postData.append('existing_previous_reports_ids[]', report.id);
                    }else{
                        postData.append('previous_reports[]', report);
                    }
                }
            }

            setNextBtnDisabled(true);
            setIsLoading(true);
            const response = await api.post('/'+userRole+'/book-appointment', postData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response?.data?.result === 'success') {
                showSuccess(response.data.message);
                setAppointmentId(response.data.data.appointment_id);
                setPaymentInitData({
                    appointment_id: response.data.data.appointment_id,
                    doctor_name: selectedDoctor?.name || '',
                    consultation_fee: response.data.data.total_amount || selectedDoctor?.consultation_fee || '',
                    payment_method: paymentMethod,
                });
                if(next_step){
                    setCurrentStep(next_step);
                }
                return true;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Error updating profile');
            return false;
        } finally {
            setNextBtnDisabled(false);
            setIsLoading(false);
            return false;
        }
    };

    const getPaymentInitUrl = async (appointment_id) => {
        try{
            setNextBtnDisabled(true);
            setIsLoading(true);
            const postData = new FormData();
            postData.append('appointment_id', appointment_id);
            const response = await api.post('/'+userRole+'/init-phi-payment', postData);
            if (response?.data?.result === 'success') {
                showSuccess(response.data.message);
                setPaymentInitData(response.data.data);
                if (response.data.data.payment_url) {
                    window.location.href = response.data.data.payment_url;
                }
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Error while getting initiate payment');
        } finally {
            setNextBtnDisabled(false);
            setIsLoading(false);
        }
    };

    const loadRazorpayCheckoutScript = () => {
        return new Promise((resolve) => {
            if (window.Razorpay) {
                resolve(true);
                return;
            }

            const existingScript = document.getElementById('razorpay-checkout-js');
            if (existingScript) {
                existingScript.addEventListener('load', () => resolve(true));
                existingScript.addEventListener('error', () => resolve(false));
                return;
            }

            const script = document.createElement('script');
            script.id = 'razorpay-checkout-js';
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.async = true;
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const verifyRazorpayPayment = async (gatewayData, razorpayResponse) => {
        try {
            setNextBtnDisabled(true);
            setIsLoading(true);
            const postData = new FormData();
            postData.append('appointment_id', gatewayData.appointment_id);
            postData.append('razorpay_payment_id', razorpayResponse.razorpay_payment_id);
            postData.append('razorpay_order_id', razorpayResponse.razorpay_order_id);
            postData.append('razorpay_signature', razorpayResponse.razorpay_signature);

            const response = await api.post('/'+userRole+'/verify-razorpay-payment', postData);
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                setCurrentStep(3);
                setAppointmentData(prev => ({
                    ...prev,
                    payment_status: 'paid',
                    id: response?.data?.data?.appointment_id,
                    paid_amount: response?.data?.data?.paid_amount,
                    appointment_date_time: response?.data?.data?.appointment_date_time,
                }));
                if(response?.data?.data?.redirect_url){
                    window.location.href = response?.data?.data?.redirect_url;
                }
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Payment verification failed.');
            }
        } catch (error) {
            console.log(error);
            showError(error.response?.data?.message || 'Error while checking payment status');
        } finally {
            setNextBtnDisabled(false);
            setIsLoading(false);
        }
    };

    const checkRazorpayPaymentStatus = async (appointment_id) => {
        try {
            setNextBtnDisabled(true);
            setIsLoading(true);
            const postData = new FormData();
            postData.append('appointment_id', appointment_id);
            const response = await api.post('/'+userRole+'/check-razorpay-payment-status', postData);
            if (response?.data?.result === 'success') {
                showSuccess(response.data.message);
                if (response.data.data.payment_status === 'paid') {
                    setCurrentStep(3);
                    setAppointmentData(prev => ({
                        ...prev,
                        payment_status: 'paid'
                    }));
                }
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Error while checking payment status');
        } finally {
            setNextBtnDisabled(false);
            setIsLoading(false);
        }
    };

    const initiateRazorpayPayment = async (appointment_id) => {
        try {
            setNextBtnDisabled(true);
            setIsLoading(true);
            const postData = new FormData();
            postData.append('appointment_id', appointment_id);
            const response = await api.post('/'+userRole+'/init-razorpay-payment', postData);
            if (response?.data?.result !== 'success') {
                showError(response?.data?.message || 'Unable to initiate Razorpay payment');
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item) => showError(item));
                }
                return;
            }

            const gatewayData = response.data.data;
            setPaymentInitData(gatewayData);
            const scriptLoaded = await loadRazorpayCheckoutScript();
            if (!scriptLoaded || !window.Razorpay) {
                showError('Unable to load Razorpay checkout');
                return;
            }

            const razorpayOptions = {
                key: gatewayData.payment_gateway_key,
                amount: Math.round((gatewayData.amount || gatewayData.consultation_fee || 0) * 100),
                currency: gatewayData.currency || 'INR',
                name: 'Varosa',
                description: 'Appointment Consultation Fee For #'+appointment_id,
                order_id: gatewayData.order_id,
                prefill: {
                    name: user?.name || '',
                    email: user?.email || '',
                    contact: `${user?.phone_code || ''}${user?.phone_number || user?.phone || ''}`.trim(),
                },
                theme: {
                    color: '#0d6efd',
                },
                handler: async (razorpayResponse) => {
                    await verifyRazorpayPayment(gatewayData, razorpayResponse);
                },
                modal: {
                    ondismiss: () => {
                        showError('Payment window closed before completion');
                    }
                }
            };

            const razorpay = new window.Razorpay(razorpayOptions);
            razorpay.open();
        } catch (error) {
            showError(error.response?.data?.message || 'Error while initiating Razorpay payment');
        } finally {
            setNextBtnDisabled(false);
            setIsLoading(false);
        }
    };

    const checkPaymentStatus = async (appointment_id) => {
        try{
            setNextBtnDisabled(true);
            setIsLoading(true);
            const postData = new FormData();
            postData.append('appointment_id', appointment_id);
            const response = await api.post('/'+userRole+'/check-phi-payment-status', postData);
            if (response?.data?.result === 'success') {
                showSuccess(response.data.message);
                if(response.data.data.payment_status === 'paid'){
                    setCurrentStep(4);
                }
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Error while getting initiate payment');
        } finally {
            setNextBtnDisabled(false);
            setIsLoading(false);
        }
    };

    const handleNextStep = async (step) => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
        if(step === 1){
            setSelectedDoctor(null);
        } else if(step === 2){
            if(!previousSelectedDoctor){
                if(!selectedDoctor || !selectedSlot){
                    showError('Please select a doctor and time slot');
                    return;
                }
                await handleMedicalQuestionsSubmit(step);
                return;
            }
        } else if(step === 3){
            if (paymentMethod === 'razorpay') {
                await initiateRazorpayPayment(appointmentId);
                return;
            }
            if (paymentInitData?.payment_url) {
                window.location.href = paymentInitData.payment_url;
                return;
            }
            await getPaymentInitUrl(appointmentId);
            return;
        }
        setCurrentStep(step);
    }

    const handleBackStep = (step) => {
        setCurrentStep(step);
    }

    return (
        <>
            <div className="container-fluid py-5 login-container">
                <div className="container">
                    <div className="row g-5">
                        {/* Appointment Booking Form */}
                        <div className="col-12 mb-4">
                            <div className="bg-light rounded p-4">
                                <h2 className="mb-0">
                                    {appointmentId ? 'Appointment Initiated' : 'Book an Appointment'}
                                </h2>
                                {appointmentId && (
                                    <p className="text-success">
                                        Your appointment ID is: <strong>{appointmentId}</strong>
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="row g-3">
                        <div className="col-12">
                            <ul id="progressbar" className="three-step">
                                <li className={currentStep >= 1 ? 'active' : ''} id="account"><strong>Select Doctor</strong></li>
                                <li className={currentStep >= 2 ? 'active' : ''} id="payment"><strong>Payment</strong></li>
                                <li className={currentStep >= 3 ? 'active' : ''} id="confirm"><strong>Finish</strong></li>
                            </ul>
                        </div>
                    </div>
                    <div className={`row d-none`}>
                        {/* Basic Information */}
                        <div className="col-md-12">
                            <label className="form-label">Reason of visit</label>
                            <input
                                type="text"
                                className="form-control py-3 border-primary"
                                name="visit_reason"
                                value={medicalQuestions.visit_reason}
                                onChange={handleMedicalQuestionChange}
                                required
                            />
                            {medicalQuestionError.visit_reason && <div className="invalid-feedback d-block">Please enter a valid reason of visit.</div>}
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Name</label>
                            <input
                                type="text"
                                className="form-control  py-3 border-primary"
                                name="name"
                                value={medicalQuestions.name}
                                onChange={handleMedicalQuestionChange}
                                required
                            />
                            {medicalQuestionError.name && <div className="invalid-feedback d-block">Please enter a valid name.</div>}
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">DOB</label>
                            <input
                                type="date"
                                className="form-control py-3 border-primary"
                                name="dob"
                                value={medicalQuestions.dob}
                                onChange={handleMedicalQuestionChange}
                                required
                                max={new Date().toISOString().split('T')[0]}
                            />
                            {medicalQuestionError.dob && <div className="invalid-feedback d-block">Please enter a valid DOB (at least 18 years old).</div>}
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Gender</label>
                            <select
                                className="form-select py-3 border-primary"
                                name="gender"
                                value={medicalQuestions.gender}
                                onChange={handleMedicalQuestionChange}
                                required
                            >
                                <option value="">Choose...</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                            </select>
                            {medicalQuestionError.gender && <div className="invalid-feedback d-block">Please select a valid gender.</div>}
                        </div>

                        {/* Address Fields */}
                        <div className="col-md-6">
                            <label className="form-label">Address Line 1</label>
                            <input
                                type="text"
                                className="form-control py-3 border-primary"
                                name="address.line1"
                                value={medicalQuestions.address.line1}
                                onChange={handleMedicalQuestionChange}
                                required
                            />
                            {medicalQuestionError.address.line1 && <div className="invalid-feedback d-block">Please enter a valid address.</div>}
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Address Line 2 (optional)</label>
                            <input
                                type="text"
                                className="form-control py-3 border-primary"
                                name="address.line2"
                                value={medicalQuestions.address.line2}
                                onChange={handleMedicalQuestionChange}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">District</label>
                            <input
                                type="text"
                                className="form-control py-3 border-primary"
                                name="address.district"
                                value={medicalQuestions.address.district}
                                onChange={handleMedicalQuestionChange}
                                required
                            />
                            {medicalQuestionError.address.district && <div className="invalid-feedback d-block">Please enter a valid district.</div>}
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">State</label>
                            <select
                                className="form-select py-3 border-primary"
                                name="address.state"
                                value={medicalQuestions.address.state}
                                onChange={handleMedicalQuestionChange}
                                required
                            >
                                <option value="">Choose...</option>
                                {indStates.map((val) => (
                                    <option key={val.key} value={val.key}>
                                        {val.name}
                                    </option>
                                ))}
                            </select>
                            {medicalQuestionError.address.state && <div className="invalid-feedback d-block">Please select a valid state.</div>}
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">Pincode</label>
                            <input
                                type="text"
                                className="form-control py-3 border-primary"
                                name="address.pincode"
                                value={medicalQuestions.address.pincode}
                                onChange={handleMedicalQuestionChange}
                                required
                            />
                            {medicalQuestionError.address.pincode && <div className="invalid-feedback d-block">Please enter a valid pincode (6-digits).</div>}
                        </div>

                        {/* Contact Information */}
                        <div className="col-md-6">
                            <label className="form-label">Email ID (optional)</label>
                            <input
                                type="email"
                                className="form-control py-3 border-primary"
                                name="email"
                                value={medicalQuestions.email}
                                onChange={handleMedicalQuestionChange}
                            />
                            {medicalQuestionError.email && <div className="invalid-feedback d-block">Please enter a valid email.</div>}
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Contact Number</label>
                            <div className="input-group mb-3">
                                <select 
                                    className="form-select py-3 border-primary"
                                    name="phoneCode"
                                    value={medicalQuestions.phoneCode}
                                    onChange={handleMedicalQuestionChange}
                                    style={{ maxWidth: '120px' }}
                                    disabled={true}
                                >
                                    <option>Choose...</option>
                                    {countryCodes.map((country) => (
                                        <option key={country.code} value={country.code}>
                                            {country.code}
                                        </option>
                                    ))}
                                </select>
                                <input
                                    type="tel"
                                    className="form-control py-3 border-primary"
                                    name="phoneNumber"
                                    value={medicalQuestions.phoneNumber}
                                    onChange={handleMedicalQuestionChange}
                                    readOnly
                                />
                            </div>
                        </div>

                        {/* Medical Information */}
                        <div className="col-12">
                            <label className="form-label">Allergies (optional)</label>
                            <textarea
                                className="form-control py-3 border-primary"
                                name="allergy"
                                value={medicalQuestions.allergy}
                                onChange={handleMedicalQuestionChange}
                                rows="3"
                            ></textarea>
                            {medicalQuestionError.allergy && <div className="invalid-feedback d-block">Please enter a valid allergy.</div>}
                        </div>
                        <div className="col-12">
                            <label className="form-label">Regular Medicine List (optional)</label>
                            <textarea
                                className="form-control py-3 border-primary"
                                name="regularMedicines"
                                value={medicalQuestions.regularMedicines}
                                onChange={handleMedicalQuestionChange}
                                rows="3"
                            ></textarea>
                            {medicalQuestionError.regularMedicines && <div className="invalid-feedback d-block">Please enter a valid regular medicine list.</div>}
                        </div>

                        {/* File Upload */}
                        <div className="col-12">
                            <label className="form-label">Previous Reports and Prescriptions (optional)</label>
                            <input
                                type="file"
                                className="form-control py-3 border-primary"
                                onChange={handleFileUpload}
                                multiple
                                accept=".pdf,.jpg,.jpeg,.png"
                                ref={reportFileInputRef}
                            />
                            <div className="text-muted mt-2 mb-2">
                                <small>PDF, JPEG, JPG, or PNG. Max size 2MB per file.</small>
                            </div>
                            {medicalQuestions.previousReports?.length > 0 && (
                                <div className="mt-2">
                                    <p>Selected files:</p>
                                    <ul className="list-group">
                                        {medicalQuestions.previousReports.map((file, index) => (
                                            file.original_name ? 
                                            (<li key={index} className="list-group-item">
                                                {file.original_name} ({formatBytes(file.file_size)})
                                                <span className="float-end">
                                                    <i className="fas fa-eye text-primary me-3" 
                                                        onClick={() => viewReport(file.file_path, true)}
                                                        style={{cursor: 'pointer'}}></i>
                                                    <i className="fas fa-trash-alt text-danger" 
                                                        onClick={() => handleRemoveFile(index)} 
                                                        style={{cursor: 'pointer'}}></i>
                                                </span>
                                            </li>)
                                            : (<li key={index} className="list-group-item">
                                                {file.name} ({formatBytes(file.size)})
                                                <span className="float-end">
                                                    <i className="fas fa-eye text-primary me-3" 
                                                        onClick={() => viewReport(file, false)}
                                                        style={{cursor: 'pointer'}}></i>
                                                    <i className="fas fa-trash-alt text-danger" 
                                                        onClick={() => handleRemoveFile(index)} 
                                                        style={{cursor: 'pointer'}}></i>
                                                </span>
                                            </li>)
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={`account-tab row g-3 ${currentStep !== 1 ? 'd-none' : '' }`}>
                        {previousSelectedDoctor ? (
                            <div className="col-md-12 wow fadeInUp" data-wow-delay="0.1s">
                                <div className="team-item rounded">
                                    <div className="team-content text-center border border-primary rounded p-4">
                                        <div className="row">
                                            <div className="col-md-5">
                                                <div className="team-img rounded">
                                                    <img src={previousSelectedDoctor.profile_picture || (getPublicImgUrl()+'/profile_8747415.png')} className="img-fluid rounded" alt="" />
                                                </div>
                                            </div>
                                            <div className="col-md-7 text-start">
                                                <h5 className="mb-2">{previousSelectedDoctor.name}</h5>
                                                {previousSelectedDoctor.qualification && <p className="mb-3">Doctor Qualification: {previousSelectedDoctor.qualification}</p>}
                                                <p className="mb-3">Doctor Bio: {previousSelectedDoctor.bio}</p>
                                                <p className="mb-3">Specializations: {previousSelectedDoctor.specializations.map((specialization) => (
                                                    <span key={specialization} className="badge bg-primary me-1">{specialization}</span>
                                                ))}</p>
                                                <p className="mb-3">Consultation Fees: <b>&#8377;{previousSelectedDoctor.consultation_fee}</b></p>
                                            </div>
                                        </div>
                                        <hr />
                                        <p><b>Your choosen appointment date & time: </b></p>
                                        <ul className="nav nav-pills doctor-time-slot">
                                            <li className="nav-item m-1">
                                                <button className="nav-link nav-tab-link active">
                                                    {appointmentData.appointment_date_time}
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                            <div className="col-md-12">
                                <div className="form-group mb-4">
                                    <div className="input-group mb-3" title="Search by Doctor Name / Department">
                                        <input 
                                            type="text" 
                                            className="form-control py-3 border-primary bg-transparent" 
                                            placeholder="Search by Doctor Name / Department" 
                                            onChange={(e) => {
                                                debounced(e.target.value);
                                            }}
                                            defaultValue={searchParams.get('t') || ''}
                                        />
                                        <button type="button" className="btn btn-primary text-white py-1 px-3">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-search" viewBox="0 0 16 16">
                                                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            {doctorList.map((doctor) => (
                                <div className="col-md-12 wow fadeInUp" data-wow-delay="0.1s" key={doctor.id}>
                                    <div className="team-item rounded">
                                        <div className="team-content text-center border border-primary rounded p-4">
                                            <div className="row">
                                                <div className="col-md-5">
                                                    <div className="team-img rounded">
                                                        <img src={doctor.profile_picture || (getPublicImgUrl()+'/profile_8747415.png')} className="img-fluid rounded" alt="" />
                                                    </div>
                                                </div>
                                                <div className="col-md-7 text-start">
                                                    <h5 className="mb-2">{doctor.name}</h5>
                                                    {doctor.bio && (<p className="mb-3">
                                                        <b>Doctor Bio:</b> {doctor.bio?.length > 100 ? doctor.bio.slice(0, 100) : doctor.bio}
                                                        {doctor.bio?.length > 100 ? (
                                                            <>
                                                            <span className={`d-none rest-bio-${doctor.id}`}>{doctor.bio.slice(100)}</span>
                                                            &nbsp;&nbsp;
                                                            <button onClick={(e) => { 
                                                                e.preventDefault();
                                                                var elements = document.getElementsByClassName(`rest-bio-${doctor.id}`);
                                                                if(elements.length > 0){
                                                                    if(elements[0].classList.contains("d-none")){
                                                                        elements[0].classList.remove("d-none");
                                                                        e.target.textContent = 'Read Less';
                                                                    }else{
                                                                        elements[0].classList.add("d-none");
                                                                        e.target.textContent = 'Read More';
                                                                    }
                                                                }
                                                            }} className="btn btn-outline-success btn-sm ml-3">Read More</button>
                                                            </>
                                                        ) : ''}
                                                    </p>)}
                                                    <p className="mb-3"><b>Specializations:</b> {doctor.specializations.map((specialization) => (
                                                        <span key={specialization} className="badge bg-primary me-1">{specialization}</span>
                                                    ))}</p>
                                                    <p className="mb-3"><b>Consultation Fees: <i>&#8377;{doctor.consultation_fee}</i></b></p>
                                                </div>
                                            </div>
                                            <hr />
                                            {Object.keys(doctor.available_slots).length === 0 && <p className="text-danger">No available slots</p>}
                                            {Object.keys(doctor.available_slots).length > 0 &&
                                                <>
                                                    <p><b>Please choose date to view available time slots</b></p>
                                                    <ul className="nav nav-pills doctor-time-slot">
                                                        {Object.entries(doctor.available_slots).map(([key, value]) => (
                                                            <li key={key} className="nav-item m-1">
                                                                <button className={`nav-link nav-tab-link-${doctor.id}-${key} ${selectedDoctorDate[doctor.id] === key ? 'active' : ''}`} onClick={() => { setSelectedDoctorDate({ ...selectedDoctorDate, [doctor.id]: key }); }}>
                                                                    {moment(key).format('ddd, D MMM YYYY')}
                                                                </button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    <hr />
                                                    {Object.entries(doctor.available_slots).map(([key, value]) => (
                                                        <div key={key} className={`nav-body nab-tab-body-${doctor.id}-${key} ${selectedDoctorDate[doctor.id] === key ? 'd-block' : 'd-none'}`}>
                                                            {
                                                                value.map((time) => (
                                                                    <span key={time.id} className="time-list">
                                                                        <input type="radio" className="btn-check" name={`options-outlined-${doctor.id}-${time.id}`} id={`selected-slot-${doctor.id}-${time.id}`} checked={selectedSlot?.id === time?.id} onChange={() => { setSelectedSlot({ ...time }); setSelectedDoctor(doctor); }} />
                                                                        <label className="btn btn-outline-success m-2" htmlFor={`selected-slot-${doctor.id}-${time.id}`}>{time.start_datetime}</label>
                                                                    </span>
                                                                ))
                                                            }
                                                        </div>)
                                                    )}
                                                </>
                                            }
                                        </div>
                                    </div>
                                </div>
                            ))}
                            </>
                        )}
                        <div className="col-md-12 text-end">
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={nextBtnDisabled}
                                onClick={() => handleNextStep(2)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                    <div className={`payment-tab feature row g-3 ${currentStep !== 2 ? 'd-none' : '' }`}>
                        <div className="col-12">
                            <div className="row g-4 justify-content-center">
                                <div className="col-md-7 wow fadeInUp" data-wow-delay="0.1s">
                                    <div className="row-cols-1 feature-item p-4">
                                        <div className="col-12">
                                            <div className="feature-icon mb-4">
                                                <div className="p-3 d-inline-flex bg-white rounded">
                                                    <i className="fas fa-rupee-sign fa-4x text-primary"></i>
                                                </div>
                                            </div>
                                            <div className="feature-content d-flex flex-column">
                                                <h5 className="mb-4">{paymentInitData?.doctor_name || selectedDoctor?.name || 'Appointment Payment'}</h5>
                                                <p className="mb-3">You have to Pay <b>&#8377;{paymentInitData?.consultation_fee || appointmentData?.total_amount || selectedDoctor?.consultation_fee || '0'}</b></p>
                                                <div className="d-none">
                                                    <label className="form-label">Choose payment method</label>
                                                    <select
                                                        className="form-select py-3 border-primary"
                                                        value={paymentMethod}
                                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                                    >
                                                        <option value="phi">PHI Payment</option>
                                                        <option value="razorpay">Razorpay</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 text-center wow fadeInUp" data-wow-delay="0.2s">
                                    {paymentMethod === 'phi' && (
                                        <>
                                            <button 
                                                className="btn btn-danger rounded-pill text-white py-3 px-5 me-2" 
                                                disabled={nextBtnDisabled} 
                                                onClick={() => checkPaymentStatus(appointmentId)}
                                            >Check Payment Status</button>
                                            <button 
                                                className="btn btn-primary rounded-pill text-white py-3 px-5 me-2" 
                                                disabled={nextBtnDisabled} 
                                                onClick={() => handleNextStep(3)}
                                            >Pay with PHI</button>
                                        </>
                                    )}
                                    {paymentMethod === 'razorpay' && (
                                        <>
                                            <button
                                                className="btn btn-danger rounded-pill text-white py-3 px-5 me-2"
                                                disabled={nextBtnDisabled}
                                                onClick={() => checkRazorpayPaymentStatus(appointmentId)}
                                            >Check Last Payment Status</button>
                                            <button
                                                className="btn btn-primary rounded-pill text-white py-3 px-5"
                                                disabled={nextBtnDisabled}
                                                onClick={() => initiateRazorpayPayment(appointmentId)}
                                            >Pay with Razorpay</button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="col-6 text-left">
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={nextBtnDisabled}
                                onClick={() => handleBackStep(1)}
                            >Back</button>
                        </div>
                        <div className="col-6 text-end">
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={nextBtnDisabled}
                                onClick={() => paymentMethod === 'razorpay' ? initiateRazorpayPayment(appointmentId) : handleNextStep(3)}
                            >Next</button>
                        </div>
                    </div>
                    <div className={`confirm-tab feature row g-3 ${currentStep !== 3 ? 'd-none' : '' }`}>
                        <div className="col-12">
                            {appointmentData &&
                            <div className="row g-4 justify-content-center">
                                <div className="col-md-6 col-lg-4 col-xl-3 wow fadeInUp" data-wow-delay="0.1s">
                                    <div className="row-cols-1 feature-item p-4">
                                        <div className="col-12">
                                            <div className="feature-icon mb-4">
                                                <div className="p-3 d-inline-flex bg-white rounded">
                                                    <i className="fas fa-user-md fa-4x text-primary"></i>
                                                </div>
                                            </div>
                                            <div className="feature-content d-flex flex-column">
                                                <h5 className="mb-4">Appointment Confirmed</h5>
                                                <p className="mb-0">You have to paid <b>&#8377;{appointmentData?.paid_amount}</b></p>
                                                <p className="mb-0">Booked Date: <b>{appointmentData?.appointment_date_time}</b></p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 text-center wow fadeInUp" data-wow-delay="0.2s">
                                    <button 
                                        className="btn btn-primary rounded-pill text-white py-3 px-5" 
                                        disabled={nextBtnDisabled} 
                                        onClick={() => { 
                                            navigate(`/dashboard/patient/appointment/${appointmentData?.id}`);
                                        }}
                                    >Go to Appointment Details</button>
                                </div>
                            </div>}
                        </div>
                        <div className="col-6 text-left">
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={true}
                            >Back</button>
                        </div>
                        <div className="col-6 text-end">
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={nextBtnDisabled}
                                onClick={() => {
                                    navigate('/dashboard/patient/appointments');
                                }}
                            >Finish</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
